import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { MainLayout } from "components/Layout";
import EventPageSkeleton from "components/Skeleton/EventPageSkeleton";
import { useLang } from "hooks/useLang";
import { ArticleJsonLd, NextSeo } from "next-seo";
import React, { useState, useEffect, useRef } from "react";
import { IFinalEvent } from "types/event";
import useWindowSize from "hooks/useWindowSize";
import removeMarkdown from "utils/helpers/removeMarkdown";
import * as gtag from "../../../../lib/gtag";
import PixelIntegrationComponent from "components/Analytics/PixelIntegrationComponent";
import EventContent from "components/EventDetailsPage/EventContent";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ITicket } from "types/ticket";
import { useCartDispatch } from "hooks/useCartV2";
import { ImprovedCartActionsEnum } from "types/improved-booking";
import { fetchEvent } from "api";
import { useAnalytics } from "components/Analytics/UnifiedAnalyticsProvider";
import { queryKeys } from "utils/queryKeys";
import { InferGetServerSidePropsType } from "next";
import Head from "next/head";

const EventHero = dynamic(
  () => import("components/EventDetailsPage/EventHero"),
  {
    ssr: false,
  }
);

export type FetchEventResponse = {
  message: string;
  payload: {
    event: IFinalEvent | Event;
    tickets: ITicket[];
  };
};

// export const getServerSideProps = async ({ params }) => {
//     const data = await fetchEvent(params.eventid)
//     console.log(data, "ee")
//     if (!data.payload) {
//         return {
//             notFound: true,
//         }
//     }
//     return {
//         props: {
//             event: data?.payload?.event,
//             // tickets: data.payload.tickets,
//         },
//     }
// }

export default function NewEventDetailsPage() {
  const { isTabletOrSmaller, isDesktop } = useWindowSize();
  const { isAr } = useLang();
  const { query, push } = useRouter();
  const router = useRouter();

  const [openVerificationModal, setOpenVerificationModal] =
    useState<boolean>(false);

  const cartDispatch = useCartDispatch();
  //TODO: refactor - match the mapping in booking page
  const pathToEventIdMap: { [key: string]: string } = {
    "3x3jeddah": "6564a239d57a803c355ea0b0",
    guvali: process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID!,
    f1: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
    f: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
  };

  // Use the event ID from the URL (with mapping) or the one from SSG props
  const eventId =
    pathToEventIdMap[router?.query?.eventid as string] ||
    (router?.query?.eventid as string);
  //END TODO

  // const queryClient = useQueryClient()
  // Get cached event data if available
  // const cachedEventData = queryCl/ient.getQueryData<{ event: IFinalEvent; tickets: ITicket[] }>(queryKeys.event(eventId))

  const { data, error, isLoading } = useQuery({
    queryKey: queryKeys.event(eventId),
    queryFn: () => fetchEvent(eventId),
    // initialData: cachedEventData ?? { event, tickets },
    staleTime: 300000, // 5 minutes (Updated from 60000)
    gcTime: 600000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      return error.message !== "EVENT_NOT_FOUND" && failureCount < 3;
    },
  });

  const event = data?.payload?.event as IFinalEvent;
  console.log(event);

  const pageVisitSource = router.query.source as string;

  const { trackEvent } = useAnalytics();

  const handleAnalyticsEvent = () => {
    trackEvent(
      "source_referral",
      {
        category: "Referral",
        label: "source",
        value: pageVisitSource || "unknown",
      },
      "event",
      event._id
    );
  };

  useEffect(() => {
    if (error instanceof Error && error.message === "EVENT_NOT_FOUND") {
      router.push("/404");
    }
  }, [error, router]);

  useEffect(() => {
    if (pageVisitSource) {
      handleAnalyticsEvent();
    }
  }, [pageVisitSource]);

  useEffect(() => {
    return () => {
      cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART });
    };
  }, [cartDispatch]);

  useEffect(() => {
    if (
      event?._id === "6343f62744f557215447e976" ||
      event?._id === "672cdbeca294b9a80a037b17"
    ) {
      push("/events/guvali");
    }
  }, [event?._id, push]);

  const updatedEvent = data?.payload?.event as IFinalEvent;

  const getImagePoster = (event: IFinalEvent) => {
    const posterUrl = event?.posters?.[0] ?? null;
    const defaultImageUrl = "/svg/v2/pass_ticket_fallback_image_light.png";

    return posterUrl
      ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterUrl}`
      : defaultImageUrl;
  };
  const bgURLDesktop = getImagePoster(updatedEvent);
  const bgURLMobile = updatedEvent?.posters[0]
    ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${updatedEvent?.posters[0]}`
    : "/svg/v2/pass_ticket_fallback_image_light.png";

  const isExpiredEvent = (): boolean => {
    const currentDate = new Date();
    const eventEndingDate = new Date(
      updatedEvent?.event_date?.ending_date || ""
    );
    if (currentDate < eventEndingDate) {
      return true;
    } else if (currentDate > eventEndingDate) {
      return false;
    }
    return false;
  };
  const isExpired = isExpiredEvent();

  const pageTitle = `${
    isAr ? updatedEvent?.ar_title || "" : updatedEvent?.en_title || ""
  }`;
  // Create plain event title without site name
  const eventTitle = isAr ? updatedEvent?.ar_title : updatedEvent?.en_title;

  const description = isAr
    ? updatedEvent?.ar_description
    : updatedEvent?.en_description || eventTitle;
  const cleanDescription = removeMarkdown(description);
  const pageDescription = cleanDescription.slice(0, 160);
  const shortDescription = cleanDescription.slice(0, 100);

  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}events/${updatedEvent?._id}`;
  const imageUrl = getImagePoster(updatedEvent);

  const formatDateISO = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString();
  };

  const startDate = formatDateISO(
    updatedEvent?.event_date?.starting_date.toString()
  );
  const endDate = formatDateISO(
    updatedEvent?.event_date?.ending_date.toString()
  );

  const keywords = `${updatedEvent?.en_title}, ${updatedEvent?.ar_title}, 
    tickets, events, Saudi Arabia, ${isAr ? "باس تيكيتس" : "PassTickets"}`;

  if (query.shared) {
    gtag.event({
      action: "view_item",
      category: "event",
      label: `share_event_${updatedEvent?._id}`,
      value: `${query.shared}`,
    });
  }

  const heroRef = useRef<HTMLDivElement | null>(null);
  const [heroHeight, setHeroHeight] = useState(0);

  useEffect(() => {
    if (heroRef.current) {
      const updateHeroHeight = () => {
        setHeroHeight(heroRef.current?.offsetHeight || 0);
      };

      updateHeroHeight();
      window.addEventListener("resize", updateHeroHeight);

      return () => {
        window.removeEventListener("resize", updateHeroHeight);
      };
    }
  }, []);

  if (isLoading) {
    return (
      <MainLayout>
        <EventPageSkeleton />
      </MainLayout>
    );
  }

  if (error) {
    console.error("Error loading event data:", error);
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-screen">
          <h2 className="text-xl mb-4">Failed to load event data</h2>
          <button
            className="bg-mint-green-500 hover:bg-mint-green-600 text-white px-6 py-2 rounded-full"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <>
      <Head>
        <link rel="alternate" hrefLang="en" href={`${canonicalUrl}?lang=en`} />
        <link rel="alternate" hrefLang="ar" href={`${canonicalUrl}?lang=ar`} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <NextSeo
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        openGraph={{
          type: "website",
          locale: isAr ? "ar_SA" : "en_US",
          url: canonicalUrl,
          title: eventTitle,
          description: pageDescription,
          site_name: isAr ? "باس تيكيتس" : "PassTickets",
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: eventTitle,
              type: "image/jpeg",
            },
          ],
        }}
        twitter={{
          handle: "@passtickets",
          site: "@passtickets",
          cardType: "summary_large_image",
        }}
        additionalMetaTags={[
          {
            name: "keywords",
            content: keywords,
          },
          {
            name: "author",
            content: isAr ? "باس تيكيتس" : "PassTickets",
          },
        ]}
      />

      {/* Event JSON-LD Structured Data */}
      <ArticleJsonLd
        url={canonicalUrl}
        title={eventTitle}
        images={[imageUrl]}
        datePublished={startDate}
        dateModified={new Date().toISOString()}
        authorName={isAr ? "باس تيكيتس" : "PassTickets"}
        publisherName={isAr ? "باس تيكيتس" : "PassTickets"}
        publisherLogo={`${process.env.NEXT_PUBLIC_SITE_URL}logo.png`}
        description={shortDescription}
      />

      {/* Event Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: eventTitle,
            startDate: startDate,
            endDate: endDate,
            description: shortDescription,
            image: imageUrl,
            location: {
              "@type": "Place",
              name: updatedEvent?.organisation?.en_name || "Saudi Arabia",
              address: {
                "@type": "PostalAddress",
                addressLocality: updatedEvent?.address?.city || "Saudi Arabia",
              },
            },
            offers: {
              "@type": "Offer",
              url: canonicalUrl,
              availability: isExpired
                ? "https://schema.org/InStock"
                : "https://schema.org/SoldOut",
              priceCurrency: "SAR",
              validFrom: startDate,
            },
            organizer: {
              "@type": "Organization",
              name: isAr ? "باس تيكيتس" : "PassTickets",
              url: process.env.NEXT_PUBLIC_SITE_URL,
            },
          }),
        }}
      />

      <PixelIntegrationComponent>
        <MainLayout>
          {/*------ Event Hero ------*/}
          <div
            ref={heroRef}
            className="fixed left-0 top-0 -z-10 aspect-video max-h-screen lg:h-screen w-full overflow-hidden md:block"
            style={{ height: isDesktop ? "100vh" : "56.25vw" }}
          >
            <EventHero
              event={updatedEvent}
              backgroundImage={isTabletOrSmaller ? bgURLMobile : bgURLDesktop}
            />
          </div>
          <div
            className="transition-all duration-300 mx-auto max-w-[1200px]"
            style={{
              marginTop: isDesktop ? "calc(100vh - 100px)" : "56.25vw",
            }}
          >
            {/*------ Event Content ------*/}
            <EventContent
              event={updatedEvent}
              isExpiredEvent={isExpired}
              openVerificationModal={openVerificationModal}
              setOpenVerificationModal={setOpenVerificationModal}
            />
          </div>
        </MainLayout>
      </PixelIntegrationComponent>
    </>
  );
}
